import axios from 'axios'
import { apiConfig } from './apiConfig'

class OptimizedHttpClient {
  private client: any
  private readonly maxConcurrentRequests = 6 // Browser limit per domain
  private activeRequests = 0
  
  constructor() {
    // Create optimized axios instance with connection pooling
    this.client = axios.create({
      baseURL: apiConfig.apiBaseUrl,
      timeout: 60000, // Increased to 60 seconds for LLM processing
      // Compression support
      decompress: true,
      maxRedirects: 3,
      validateStatus: (status: number) => status < 500, // Cache 4xx errors too
    })

    // Add auth token to requests when logged in
    this.client.interceptors.request.use((config: any) => {
      const token = localStorage.getItem('wital_token')
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })
  }


  

  
  // Public API methods
  async get(url: string, config?: any): Promise<any> {
    if (import.meta.env.DEV) console.log(`🌐 Network request for ${url}`)
    this.activeRequests++
    
    try {
      const response = await this.client.get(url, config)
      this.activeRequests = Math.max(0, this.activeRequests - 1)
      return response
    } catch (error) {
      this.activeRequests = Math.max(0, this.activeRequests - 1)
      throw error
    }
  }
  
  async post(url: string, data?: any, config?: any): Promise<any> {
    return this.client.post(url, data, config)
  }

  async patch(url: string, data?: any, config?: any): Promise<any> {
    return this.client.patch(url, data, config)
  }

  async delete(url: string, config?: any): Promise<any> {
    return this.client.delete(url, config)
  }
  
  // Weather-specific optimized methods - always use WITAL endpoint
  async getWeatherForecast(params: {
    location: string
    date: string
    length?: number
    language?: string
    audience?: string
    country_code?: string
    model?: string
    llm?: string
  }): Promise<any> {
    // Always use sciapis.com API in production, local backend for development
    if (import.meta.env.DEV) {
      console.log(`🤖 Using WITAL endpoint: ${params.location} ${params.date}`)
      console.log(`🤖 WITAL params:`, params)
      console.log(`🤖 API Base URL:`, apiConfig.apiBaseUrl)
    }
    
    let url = apiConfig.buildUrl('/api/forecast')
    
    const searchParams = new URLSearchParams()
    searchParams.append('location', params.location)
    searchParams.append('date', params.date)
    if (params.length) searchParams.append('length', params.length.toString())
    if (params.language) {
      searchParams.append('language', params.language);
    }
    if (params.audience) searchParams.append('audience', params.audience)
    if (params.country_code) searchParams.append('country_code', params.country_code.toUpperCase())
    if (params.model) searchParams.append('model', params.model)
    if (params.llm) searchParams.append('llm', params.llm)
    searchParams.append('debug', 'false')
    
    url += '?' + searchParams.toString()
    
    // Debug the final API call parameters
    if (import.meta.env.DEV) {
      console.log('🌐 HTTP Client: Final WITAL API call:', {
        url,
        originalParams: params,
        searchParams: Object.fromEntries(searchParams.entries())
      })
    }
    
    // Make the request without passing params again (already in URL)
    const response = await this.get(url)
    return response.data
  }
  

}

// Create singleton instance
const httpClient = new OptimizedHttpClient()

export default httpClient
export type { OptimizedHttpClient } 